import React, { useMemo } from 'react'
import { format } from 'date-fns'
import { makeStyles } from '@material-ui/core/styles'
import { ExpansionPanel, ExpansionPanelDetails, ExpansionPanelSummary, Box } from '@material-ui/core'
import { ExpandMore } from '@material-ui/icons'

import { useSelectors } from '../../../../store'
import { PriceText } from '../../../../lib/price-text'
import { addressWithPostcode } from '../../../../lib/postcode'

import { styles } from './styles'
import { money } from '../../../../lib/gbp'
import { codeLabel, codeFloorHint } from '../../../../lib/promo-credit'
import { sameDaySlotLabel } from '../../../../lib/same-day'

// Once per module: makeStyles called inside a render built a new JSS sheet,
// with new class names, on every render (booking screen, 11 Oct 2026).
const useStyles = makeStyles(styles)

export const ServicesSummary = React.memo(({ defaultExpanded, expanded, sidebar, embedded }) => {
  const classes = useStyles()
  const {
    address,
    postcode,
    date,
    sameDay,
    timeSlot,
    carRegistration,
    carModel,
    baseService,
    prices,
    agentTip,
    agentTipPrice,
    promoCode,
    percentDiscountPrice,
    creditDiscountPrice,
    codeDiscountPrice,
    promoCodeInfo,
    servicesPrice,
    price,
    sortedSelectedServiceIds
  } = useSelectors()

  const accountCreditPrice = creditDiscountPrice.subtract(codeDiscountPrice)
  const floorHint = codeFloorHint(promoCodeInfo, servicesPrice.intValue, money(promoCodeInfo?.value || 0), money(promoCodeInfo?.min_basket || 0))

  const dateLine = useMemo(() => {
    if (!date) return ''
    // Same-day reads "Today, 2pm to 7pm", as the confirmation does.
    if (sameDay) return sameDaySlotLabel()
    const datePart = format(date, 'eeee dd MMM yyyy')
    const timePart = timeSlot
    return timePart ? `${datePart} · ${timePart}` : datePart
  }, [date, sameDay, timeSlot])

  const vehicleLine = useMemo(() => {
    if (!carModel && !carRegistration) return ''
    return [carModel, carRegistration].filter(Boolean).join(' · ')
  }, [carModel, carRegistration])

  // Extras = selected services minus the base service
  const extraServices = useMemo(() => {
    return sortedSelectedServiceIds
      .filter(id => prices.byId[id] && prices.byId[id].id !== baseService?.id)
      .map(id => prices.byId[id])
      .filter(Boolean)
  }, [sortedSelectedServiceIds, prices.byId, baseService])

  // The form holds the house number or name and the postcode apart; show
  // them as the order will store them, "12, SW6 1RE".
  const cleanAddress = address ? addressWithPostcode(address.replace(/,?\s*UK$/i, ''), postcode) : ''

  // Skeleton for the total until /api/v2/services has answered (the total
  // is a meaningless £0.00 before then).
  const totalText = prices.ids.length > 0 ? money(price) : <PriceText value={null} />

  // Desktop sidebar (or `embedded` when rendered inside the mobile bottom sheet -
  // it provides its own header and we don't want a doubled card chrome).
  if (sidebar) {
    return (
      <div className={classes.sidebarWrap}>
        {!embedded && <div className={classes.sidebarLabel}>Your order</div>}
        <div className={embedded ? classes.sidebarCardEmbedded : classes.sidebarCard}>
          {baseService && (
            <div className={classes.serviceHeader}>
              <span className={classes.serviceName}>{baseService.name}</span>
              <span className={classes.servicePrice}>{money(baseService.price)}</span>
            </div>
          )}

          {(cleanAddress || dateLine || vehicleLine) && (
            <div className={classes.details}>
              {cleanAddress && (
                <div className={classes.detail}>
                  <span className={classes.detailLabel}>Location</span>
                  <span className={classes.detailValue}>{cleanAddress}</span>
                </div>
              )}
              {dateLine && (
                <div className={classes.detail}>
                  <span className={classes.detailLabel}>When</span>
                  <span className={classes.detailValue}>{dateLine}</span>
                </div>
              )}
              {vehicleLine && (
                <div className={classes.detail}>
                  <span className={classes.detailLabel}>Vehicle</span>
                  <span className={classes.detailValue}>{vehicleLine}</span>
                </div>
              )}
            </div>
          )}

          {(extraServices.length > 0 || agentTip || percentDiscountPrice.intValue > 0 || creditDiscountPrice.intValue > 0) && (
            <div className={classes.extras}>
              {extraServices.map(svc => (
                <div className={classes.extraRow} key={`extra-${svc.id}`}>
                  <span>{svc.name}</span>
                  <span>{money(svc.price)}</span>
                </div>
              ))}
              {agentTip && (
                <div className={classes.extraRow}>
                  <span>Tip for your valeter</span>
                  <span>{money(agentTipPrice)}</span>
                </div>
              )}
              {percentDiscountPrice.intValue > 0 && (
                <div className={classes.discountRow}>
                  <span>{promoCode ? `${promoCode.value}% promo` : 'Discount'}</span>
                  <span>−{money(percentDiscountPrice)}</span>
                </div>
              )}
              {codeDiscountPrice.intValue > 0 && (
                <div className={classes.discountRow}>
                  <span>{codeLabel(promoCodeInfo, money(codeDiscountPrice))}</span>
                  <span>−{money(codeDiscountPrice)}</span>
                </div>
              )}
              {accountCreditPrice.intValue > 0 && (
                <div className={classes.discountRow}>
                  <span data-testid="credit-applied">{`${money(accountCreditPrice)} credit applied`}</span>
                  <span>−{money(accountCreditPrice)}</span>
                </div>
              )}
            </div>
          )}

          {floorHint && <div className={classes.extraRow}>{floorHint}</div>}

          <div className={classes.totalWrap}>
            <span className={classes.totalLabel}>Total</span>
            <span className={classes.totalAmount}>{totalText}</span>
          </div>
        </div>
      </div>
    )
  }

  // Mobile accordion
  return (
    <ExpansionPanel className={classes.brief} defaultExpanded={defaultExpanded} expanded={expanded}>
      <ExpansionPanelSummary className={classes.briefSummary} expandIcon={<ExpandMore />}>
        <div className={classes.mobileHeader}>
          <span className={classes.mobileHeaderName}>{baseService?.name || 'Order'}</span>
          <span className={classes.mobileHeaderPrice}>{totalText}</span>
        </div>
      </ExpansionPanelSummary>
      <ExpansionPanelDetails className={classes.briefDetails}>
        <Box className={classes.sidebarCard} style={{ width: '100%', padding: 16, boxShadow: 'none', border: '1px solid #eef0f3' }}>
          {baseService && (
            <div className={classes.serviceHeader}>
              <span className={classes.serviceName}>{baseService.name}</span>
              <span className={classes.servicePrice}>{money(baseService.price)}</span>
            </div>
          )}
          {(cleanAddress || dateLine || vehicleLine) && (
            <div className={classes.details}>
              {cleanAddress && (
                <div className={classes.detail}>
                  <span className={classes.detailLabel}>Location</span>
                  <span className={classes.detailValue}>{cleanAddress}</span>
                </div>
              )}
              {dateLine && (
                <div className={classes.detail}>
                  <span className={classes.detailLabel}>When</span>
                  <span className={classes.detailValue}>{dateLine}</span>
                </div>
              )}
              {vehicleLine && (
                <div className={classes.detail}>
                  <span className={classes.detailLabel}>Vehicle</span>
                  <span className={classes.detailValue}>{vehicleLine}</span>
                </div>
              )}
            </div>
          )}
          {extraServices.map(svc => (
            <div className={classes.extraRow} key={`m-extra-${svc.id}`} style={{ marginTop: 8 }}>
              <span>{svc.name}</span>
              <span>{money(svc.price)}</span>
            </div>
          ))}
          <div className={classes.totalWrap}>
            <span className={classes.totalLabel}>Total</span>
            <span className={classes.totalAmount}>{totalText}</span>
          </div>
        </Box>
      </ExpansionPanelDetails>
    </ExpansionPanel>
  )
})
